/**
 * Date utility functions
 * 
 * CRITICAL: All dates are stored and compared in the user's LOCAL timezone.
 * This ensures that "today" means the user's current day, regardless of server location.
 * 
 * Example:
 * - User in Pakistan at 00:01 AM on Jan 15 → "today" = "2025-01-15"
 * - User in UK at 11:01 PM on Jan 14 → "today" = "2025-01-14"
 * 
 * This is correct behavior for a client-side PWA where each user sees their own local day.
 */

import dayjs from 'dayjs';
import { isDemoMode } from '../demo/demoDetection';
import { demoToday, demoYesterday } from '../demo/demoClock';

/**
 * Get today's date in user's local timezone (YYYY-MM-DD format)
 * 
 * In demo mode, returns the demo anchor date so all services,
 * hooks, and components see a consistent "today".
 * 
 * Always use this function instead of dayjs().format('YYYY-MM-DD')
 * to ensure consistency across the app.
 * 
 * @returns Date string in format YYYY-MM-DD (user's local timezone)
 */
export function getTodayDate(): string {
  if (isDemoMode()) return demoToday();
  return dayjs().format('YYYY-MM-DD');
}

/**
 * Get yesterday's date in user's local timezone (YYYY-MM-DD format)
 * 
 * In demo mode, returns the demo anchor yesterday.
 * 
 * @returns Date string in format YYYY-MM-DD (user's local timezone)
 */
export function getYesterdayDate(): string {
  if (isDemoMode()) return demoYesterday();
  return dayjs().subtract(1, 'day').format('YYYY-MM-DD');
}

/**
 * Check if a date string matches today (in user's local timezone)
 * 
 * @param date - Date string in YYYY-MM-DD format
 * @returns true if the date matches today in user's local timezone
 */
export function isToday(date: string): boolean {
  return date === getTodayDate();
}

/**
 * Format a date string or dayjs object to YYYY-MM-DD format
 * Uses user's local timezone
 * 
 * @param date - Date string (YYYY-MM-DD) or dayjs object
 * @returns Date string in format YYYY-MM-DD (user's local timezone)
 */
export function formatDate(date: string | dayjs.Dayjs): string {
  if (typeof date === 'string') {
    // If already in YYYY-MM-DD format, return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }
    // Otherwise parse and format
    return dayjs(date).format('YYYY-MM-DD');
  }
  return date.format('YYYY-MM-DD');
}

/**
 * Get a date N days ago in user's local timezone
 * 
 * In demo mode, uses the demo anchor as "today" so offsets are correct.
 * 
 * @param daysAgo - Number of days to subtract
 * @returns Date string in format YYYY-MM-DD (user's local timezone)
 */
export function getDateDaysAgo(daysAgo: number): string {
  const today = getTodayDate();
  return dayjs(today).subtract(daysAgo, 'day').format('YYYY-MM-DD');
}

/**
 * Check if a date can be edited (update/delete existing entries).
 * 
 * Normal behavior: Only today can be edited (days auto-close at 23:59)
 * QA mode: Yesterday can also be edited for testing purposes
 * 
 * NOTE: This is for EDIT/DELETE only. For CREATING new entries, use canCreateForDate().
 * 
 * @param date - Date string in YYYY-MM-DD format
 * @returns true if existing entries on this date can be edited
 */
export function canEditDate(date: string): boolean {
  const today = getTodayDate();
  const yesterday = getYesterdayDate();
  
  // Always allow editing today
  if (date === today) {
    return true;
  }
  
  // Check QA mode: allow editing yesterday if QA mode is enabled
  const qaModeEnabled = typeof window !== 'undefined' && 
    localStorage.getItem('coretrak.qa.allowEditYesterday') === 'true';
  
  if (qaModeEnabled && date === yesterday) {
    return true;
  }
  
  // All other dates are closed (not editable)
  return false;
}

/**
 * Check if a new entry can be CREATED for a given date.
 * 
 * Users can always create entries for today and yesterday.
 * This supports the common real-world pattern of logging yesterday's workout.
 * Older dates are not allowed (prevents accidental backdating).
 * 
 * @param date - Date string in YYYY-MM-DD format
 * @returns true if a new entry can be created for this date
 */
export function canCreateForDate(date: string): boolean {
  const today = getTodayDate();
  const yesterday = getYesterdayDate();
  return date === today || date === yesterday;
}

/**
 * Calculate journey day based on account creation date
 * 
 * Journey day = today (calendar date) - user.created_at (calendar date)
 * Uses calendar days only, starts on account creation date
 * 
 * @param creationTime - Firebase Auth user metadata.creationTime (timestamp in milliseconds)
 * @returns Journey day number (1 = first day, 2 = second day, etc.)
 */
export function calculateJourneyDay(creationTime: string | number | null | undefined): number {
  if (!creationTime) {
    return 1; // Default to day 1 if creation time is not available
  }
  
  // Convert to Date object
  const creationDate = new Date(creationTime);
  
  // Get today's date at midnight (start of day) in user's local timezone
  const today = dayjs(getTodayDate()).startOf('day');
  
  // Get creation date at midnight (start of day) in user's local timezone
  const accountCreationDate = dayjs(creationDate).startOf('day');
  
  // Calculate difference in calendar days
  const daysDiff = today.diff(accountCreationDate, 'day');
  
  // Journey day starts at 1 (first day = day 1, not day 0)
  return Math.max(1, daysDiff + 1);
}
